import { checkoutVersions, semverRegex, VERSION_PREFERRED } from '../constants';
import { TApiVersions, TCheckoutMajorVersion, TCheckoutVersion, TComgateCheckoutConfigBase, TResolvedVersion } from '../types';
import { resolveDebug, safeFetch } from '.';

/**
 * Cache for latest major version from server versions.json
 */
let serverVersions: TApiVersions | undefined;

const semverPrefix = 'v/';
const majorPrefix = '@';

/**
 * Resolve URL for Comgate Checkout script
 * @param config
 * @param cdnBaseUrl
 */
export const resolveVersion = async (config: TComgateCheckoutConfigBase, cdnBaseUrl: string): Promise<TResolvedVersion> => {
    try {
        // Try to load the versions from the server
        if (!serverVersions) {
            await safeFetch<TApiVersions>(cdnBaseUrl + 'versions.json')
                .then((res) => {
                    if (res.ok && res.isJson) {
                        serverVersions = res.data;
                    } else if (resolveDebug(config)) {
                        console.warn('Checkout SDK: Invalid versions.json response, using cdn fallback.', {
                            url: cdnBaseUrl + 'versions.json',
                            error: res?.error,
                        });
                    }
                })
                .catch((error: unknown) => {
                    if (resolveDebug(config)) {
                        console.warn("Checkout SDK: can't fetch versions.json, using cdn fallback.", { url: cdnBaseUrl + 'versions.json', error });
                    }
                });
        }

        return getExpandedVersion(config.version, serverVersions);
    } catch (error) {
        return {
            version: majorPrefix + VERSION_PREFERRED,
        };
    }
};

/**
 * Get the expanded version string based on custom version and server data.
 * @param custom
 * @param server
 */
function getExpandedVersion(custom?: TCheckoutVersion, server?: TApiVersions): TResolvedVersion {
    // If server data is not loaded, allow only @1, @2, @dev or fallback to preferred
    if (!server) {
        if (custom && semverRegex.test(custom)) {
            return { version: semverPrefix + custom }; // E.g. '2.0.4'
        }
        if (custom && checkoutVersions.includes(custom as TCheckoutMajorVersion)) {
            return { version: majorPrefix + custom }; // E.g. '@1', '@2', '@dev'
        }
        // Final fallback
        return { version: majorPrefix + VERSION_PREFERRED };
    }

    // If custom is semver (e.g. '2.0.4'), allow if a corresponding major exists
    if (custom && semverRegex.test(custom)) {
        const major = custom.split('.')[0];
        if (server.majors[major]) {
            return {
                hash: server.majors[major].hash,
                version: semverPrefix + custom,
            };
        }
    }

    // If custom matches a major, use the latest from that major
    if (custom && custom in server.majors) {
        return {
            hash: server.majors[custom].hash,
            version: semverPrefix + server.majors[custom].latest,
        };
    }

    // If the preferredVersion major exists, use the latest from that major
    if (VERSION_PREFERRED in server.majors) {
        return {
            hash: server.majors[VERSION_PREFERRED].hash,
            version: semverPrefix + server.majors[VERSION_PREFERRED].latest,
        };
    }

    // If nothing matches, fallback to a preferred version as @major
    return {
        version: majorPrefix + VERSION_PREFERRED,
    };
}
